import { useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../lib/realtime";
import { playChime } from "../lib/push";

const QUIET_MS = 1500;

function inScope(user, m) {
  const venueIds = user.scope?.venue_ids || [];
  if (!user.scope?.full_access && venueIds.length && m.venue_id && !venueIds.includes(m.venue_id)) return false;
  const myDepts = user.department_ids || [];
  if (["staff", "supervisor", "manager"].includes(user.role) && myDepts.length) {
    const evDepts = m.department_ids || (m.department_id ? [m.department_id] : []);
    if (evDepts.length && !evDepts.some((d) => myDepts.includes(d))) return false;
  }
  return true;
}

function describe(m, user) {
  const by = m.by ? `${m.by}` : "Someone";
  switch (m.type) {
    case "task.changed":
      if (m.action === "created") return { kind: "info", title: "New floor task", body: `${by} added "${m.title}"${m.due_time ? ` · due ${m.due_time}` : ""}` };
      if (m.action === "completed") return { kind: "success", title: m.on_time === false ? "Task completed late" : "Task completed", body: `${by} finished "${m.title}"` };
      if (m.action === "updated") return { kind: "info", title: "Task updated", body: `${by} changed "${m.title}"` };
      if (m.action === "deleted") return { kind: "info", title: "Task removed", body: `${by} removed "${m.title}"` };
      return null;
    case "checklist.run":
      if (m.action === "item_logged" && m.by_id !== user.id && m.result !== "fail") return { kind: "info", title: "Checklist line logged", body: `${by}: ${m.item_title} · ${m.run?.template_name || ""}` };
      if (m.action === "submitted") return { kind: "info", title: "Checklist submitted", body: `${m.run?.template_name} (${m.run?.shift}) by ${by} — awaiting verification` };
      if (m.action === "verified") return { kind: "success", title: "Checklist verified", body: `${m.run?.template_name} signed off by ${by}` };
      if (m.action === "returned") return { kind: "warning", title: "Checklist returned", body: `${m.run?.template_name} sent back by ${by}${m.note ? `: ${m.note}` : ""}` };
      if (m.action === "ai_vision" && m.matches === false) return { kind: "warning", title: "AI flagged a photo", body: "A photo proof does not match its entry — see AI Flags" };
      return null;
    case "checklist.exception":
      return { kind: m.critical ? "error" : "warning", title: m.critical ? "CRITICAL exception" : "Exception logged", body: `${m.title}: ${m.value} · ${m.template_name || ""} (${by})` };
    case "shift.state":
      return m.action === "closed" ? { kind: "info", title: "Operating day closed", body: `${by} closed ${m.closed_date}; now on ${m.operating_date}` } : null;
    case "exception.escalated":
      return { kind: "error", title: "Escalated to senior leads", body: `${m.title} — ${m.reason || "senior sign-off now required"}` };
    case "escalation.fired":
      return (m.recipients || []).includes(user.id) ? { kind: "error", title: m.level_label ? `${m.level_label} escalation` : `Escalation level ${m.level}`, body: m.title } : null;
    case "exception.resolved":
      return m.by && m.by_id !== user.id ? { kind: "success", title: "Alarm cleared", body: `${m.exception?.title} — ${m.by}` } : null;
    default:
      return null;
  }
}

export function LiveEvents() {
  const { user } = useAuth();
  const last = useRef({});
  useRealtime((m) => {
    if (!user || !m || m.type === "hello") return;
    if (m.by_id && m.by_id === user.id && !["escalation.fired"].includes(m.type)) return;
    if (!inScope(user, m)) return;
    const d = describe(m, user);
    if (!d) return;
    const ref = m.ref || m.exception?.ref || {};
    const runId = m.run_id || m.run?.id || ref.run_id;
    const itemId = m.item_id || ref.item_id;
    const exc = m.exception_id || m.exception?.id;
    const incident = m.type === "exception.resolved" ? (exc ? `res:${exc}` : null)
      : runId && itemId ? `inc:${runId}:${itemId}` : exc ? `exc:${exc}` : null;
    const key = incident || `${m.type}:${m.action || ""}:${m.task_id || m.run?.id || m.title}`;
    const now = Date.now();
    if (!incident && last.current[key] && now - last.current[key] < QUIET_MS) return;
    last.current[key] = now;
    const fn = toast[d.kind] || toast;
    fn(d.title, { description: d.body, duration: d.kind === "error" ? 9000 : 5000, id: key });
    if (d.kind === "error") { try { playChime(); } catch { /* no audio */ } }
  });
  return null;
}
